import { useState, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/contexts/LanguageContext";
import { IdentityStep } from "./IdentityStep";
import { LensStep, PrimaryInterest } from "./LensStep";
import { BenchmarkStep } from "./BenchmarkStep";
import { ConfigurationEffect } from "./ConfigurationEffect";
import { LanguageToggle } from "@/components/ui/language-toggle";
import { Rocket } from "lucide-react";
import { analytics } from "@/lib/analytics";

type Step = "identity" | "lens" | "benchmark" | "configuring";

interface OnboardingWizardProps {
  isEditMode?: boolean;
  onClose?: () => void;
}

export function OnboardingWizard({ isEditMode = false, onClose }: OnboardingWizardProps) {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t, language } = useTranslation();

  const [step, setStep] = useState<Step>("identity");
  const [fullName, setFullName] = useState(profile?.full_name ?? "");
  const [nationalOrg, setNationalOrg] = useState(profile?.jci_national_org ?? "");
  const [localOrg, setLocalOrg] = useState(profile?.jci_local_org ?? "");
  const [primaryInterest, setPrimaryInterest] = useState<PrimaryInterest | null>(profile?.primary_interest ?? null);
  const [focusCountry, setFocusCountry] = useState(profile?.focus_country ?? "");

  const handleComplete = async () => {
    if (!user) return;

    try {
      const { error } = await (supabase as any)
        .from("profiles")
        .update({
          full_name: fullName,
          jci_national_org: nationalOrg,
          jci_local_org: localOrg || null,
          primary_interest: primaryInterest,
          focus_country: focusCountry,
          onboarding_completed: true,
          preferred_language: language, // Save current language preference
        })
        .eq("id", user.id);

      if (error) throw error;

      // Track onboarding completion
      analytics.onboardingComplete(3);

      // In edit mode, just close and refresh
      if (isEditMode) {
        toast({
          title: t('onboarding.preferencesUpdated'),
          description: t('onboarding.lensUpdated'),
        });
        onClose?.();
        window.location.reload();
        return;
      }

      // Force a small delay to ensure database update is committed before navigation
      // This helps ensure the profile state is fresh when App.tsx checks onboarding_completed
      await new Promise(resolve => setTimeout(resolve, 100));

      setStep("configuring");
    } catch (err) {
      console.error("Onboarding save failed:", err);
      toast({
        title: t('onboarding.error'),
        description: t('onboarding.saveFailed'),
        variant: "destructive",
      });
    }
  };

  const handleConfigurationComplete = useCallback(() => {
    // Use navigate instead of window.location.href to preserve React context and language state
    // The language is already saved to localStorage and to the profile, so it will persist
    navigate("/dashboard", { replace: true });
  }, [navigate]);

  const stepIndicators = ["identity", "lens", "benchmark"];
  const currentStepIndex = stepIndicators.indexOf(step);

  if (step === "configuring") {
    return <ConfigurationEffect onComplete={handleConfigurationComplete} />;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <Rocket className="w-4 h-4" />
              {t('onboarding.commandCenterSetup')}
            </div>
            <LanguageToggle />
          </div>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {stepIndicators.map((s, index) => (
            <div key={s} className="flex items-center gap-2">
              <motion.div
                animate={{
                  scale: index === currentStepIndex ? 1.2 : 1,
                  backgroundColor:
                    index <= currentStepIndex
                      ? "hsl(var(--primary))"
                      : "hsl(var(--muted))",
                }}
                className="w-3 h-3 rounded-full"
              />
              {index < stepIndicators.length - 1 && (
                <div
                  className={`w-12 h-0.5 ${
                    index < currentStepIndex ? "bg-primary" : "bg-muted"
                  }`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Card container */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-lg">
          <AnimatePresence mode="wait">
            {step === "identity" && (
              <IdentityStep
                key="identity"
                fullName={fullName}
                nationalOrg={nationalOrg}
                localOrg={localOrg}
                onFullNameChange={setFullName}
                onNationalOrgChange={setNationalOrg}
                onLocalOrgChange={setLocalOrg}
                onNext={() => {
                  analytics.onboardingStep(1, 'identity', 'complete');
                  setStep("lens");
                }}
              />
            )}
            {step === "lens" && (
              <LensStep
                key="lens"
                selected={primaryInterest}
                onSelect={setPrimaryInterest}
                onNext={() => {
                  analytics.onboardingStep(2, 'lens', 'complete');
                  setStep("benchmark");
                }}
                onBack={() => setStep("identity")}
              />
            )}
            {step === "benchmark" && (
              <BenchmarkStep
                key="benchmark"
                focusCountry={focusCountry}
                onFocusCountryChange={setFocusCountry}
                onComplete={handleComplete}
                onBack={() => setStep("lens")}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Step label */}
        <p className="text-center text-sm text-muted-foreground mt-4">
          {t('onboarding.stepOf', { current: currentStepIndex + 1, total: 3 })}
        </p>
      </motion.div>
    </div>
  );
}
